import React from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Close, Language, Translate } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface BulkActionToolbarProps {
  selectedCount: number;
  totalCount: number;
  allSelected: boolean;
  loadingAllIds: boolean;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onTranslate: () => void;
  onPublish: () => void;
  publishLoading: boolean;
  onCancel: () => void;
}

export const BulkActionToolbar: React.FC<BulkActionToolbarProps> = ({
  selectedCount,
  totalCount,
  allSelected,
  loadingAllIds,
  onSelectAll,
  onDeselectAll,
  onTranslate,
  onPublish,
  publishLoading,
  onCancel,
}) => {
  const { t } = useTranslation();

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 2,
        px: 2,
        py: 1.25,
        borderColor: 'primary.light',
        bgcolor: '#f5fbf6',
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ flexGrow: 1, gap: 1 }}>
        <Chip
          label={
            selectedCount === 0
              ? t('products.bulk.noneSelected', 'No products selected')
              : t('products.bulk.selectedCount', '{{count}} selected', { count: selectedCount })
          }
          size="small"
          color={selectedCount > 0 ? 'primary' : 'default'}
          variant={selectedCount > 0 ? 'filled' : 'outlined'}
        />

        {!allSelected && totalCount > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {loadingAllIds ? (
              <CircularProgress size={14} />
            ) : (
              <Typography
                variant="body2"
                component="span"
                color="primary.dark"
                sx={{ cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted' }}
                onClick={onSelectAll}
              >
                {t('products.bulk.selectAll', 'Select all {{count}} products', { count: totalCount })}
              </Typography>
            )}
          </Box>
        )}

        {selectedCount > 0 && (
          <Typography
            variant="body2"
            component="span"
            color="text.secondary"
            sx={{ cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted' }}
            onClick={onDeselectAll}
          >
            {t('products.bulk.deselectAll', 'Deselect all')}
          </Typography>
        )}

        <Button
          variant="outlined"
          size="small"
          startIcon={<Translate fontSize="small" />}
          disabled={selectedCount === 0}
          onClick={onTranslate}
          sx={{
            borderColor: 'primary.main',
            color: 'primary.dark',
            '&:hover': { bgcolor: '#edf7ef' },
            '&.Mui-disabled': { borderColor: 'divider' },
          }}
        >
          {t('products.bulk.translate', 'Translate')}
        </Button>

        <Button
          variant="outlined"
          size="small"
          startIcon={publishLoading ? <CircularProgress size={14} /> : <Language fontSize="small" />}
          disabled={selectedCount === 0 || publishLoading}
          onClick={onPublish}
          sx={{
            borderColor: 'primary.main',
            color: 'primary.dark',
            '&:hover': { bgcolor: '#edf7ef' },
            '&.Mui-disabled': { borderColor: 'divider' },
          }}
        >
          {t('products.bulk.enableEmbedding', 'Enable embedding')}
        </Button>
      </Stack>

      <Button
        size="small"
        onClick={onCancel}
        startIcon={<Close fontSize="small" />}
        sx={{ ml: 'auto', color: 'text.secondary' }}
      >
        {t('products.bulk.cancel', 'Cancel')}
      </Button>
    </Paper>
  );
};
